import axios from 'axios';

// Función para obtener el slug del tenant de la URL
const getTenantSlug = () => {
  const pathParts = window.location.pathname.split('/').filter(p => p);

  // Lista de rutas reservadas que NO son tenants
  const reservedRoutes = ['super-admin', 'api'];

  // Si no hay partes o la primera es 'super-admin', no hay tenant
  if (pathParts.length === 0 || pathParts[0] === 'super-admin') {
    return '';
  }

  // Si la primera parte es 'login' y no hay segunda parte, no es tenant
  if (pathParts.length === 1 && pathParts[0] === 'login') {
    return '';
  }

  // Para rutas como /codigo/login, /codigo/dashboard, etc.
  // La primera parte es el código del tenant si no está en reservedRoutes
  if (!reservedRoutes.includes(pathParts[0])) {
    return pathParts[0];
  }

  return '';
};

// Construir la base URL incluyendo el tenant slug si existe
const buildBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return `${window.location.origin}/api`;
};

const API_BASE_URL = buildBaseURL();

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use(
  (config) => {
    // Actualizar baseURL dinámicamente en cada petición por si cambió la ruta
    config.baseURL = buildBaseURL();

    // Incluir el tenant slug en un header para el backend
    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      config.headers['X-Tenant'] = tenantSlug;
    }

    const token = localStorage.getItem('token');
    console.log('Token en localStorage:', token ? 'Presente' : 'NO ENCONTRADO');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('Haciendo petición a:', `${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('Error en interceptor de request:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirigir al login del tenant actual
      const tenantSlug = getTenantSlug();
      window.location.href = tenantSlug ? `/${tenantSlug}/login` : '/super-admin/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials) => {
    try {
      // Detectar tenant_code desde la URL
      const tenantSlug = getTenantSlug();

      // Incluir tenant_code en las credenciales si existe
      const loginData = {
        ...credentials,
        tenant_code: tenantSlug || 'invramirez'  // Por defecto invramirez
      };

      console.log('Login con tenant_code:', loginData.tenant_code);

      const response = await api.post('/auth/login', loginData);
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.usuario));

        // Guardar info del tenant si existe
        if (response.data.tenant) {
          localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
        }
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Redirigir al login del tenant actual
    const tenantSlug = getTenantSlug();
    window.location.href = tenantSlug ? `/${tenantSlug}/login` : '/super-admin/login';
  },
  me: () => api.get('/auth/me'),
  refreshUser: async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
      }
      return response.data;
    } catch (error) {
      console.error('No se pudo refrescar el usuario actual', error);
      throw error;
    }
  },
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  }
};

export const clienteService = {
  getAll: (params = {}) => api.get('/clientes', { params }),
  getClientes: (params = {}) => api.get('/clientes', { params }),
  getCliente: (id) => api.get(`/clientes/${id}`),
  getById: (id) => api.get(`/clientes/${id}`),
  getClienteDetalle: (id) => api.get(`/clientes/${id}/detalle`),
  createCliente: (data) => api.post('/clientes', data),
  create: (data) => api.post('/clientes', data),
  updateCliente: (id, data) => api.put(`/clientes/${id}`, data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  deleteCliente: (id) => api.delete(`/clientes/${id}`),
  delete: (id) => api.delete(`/clientes/${id}`),
  eliminarPermanente: (id) => api.delete(`/clientes/${id}/eliminar-permanente`),  // NUEVO: Eliminación permanente
  validarCedula: (cedula) => api.get(`/clientes/validar-cedula/${cedula}`),
  getReferencias: (clienteId) => api.get(`/clientes/${clienteId}/referencias`),
  addReferencia: (clienteId, data) => api.post(`/clientes/${clienteId}/referencias`, data),
  updateReferencia: (referenciaId, data) => api.put(`/referencias/${referenciaId}`, data),
  deleteReferencia: (referenciaId) => api.delete(`/referencias/${referenciaId}`),
  getCobradores: () => api.get('/clientes/cobradores')  // Obtener lista de cobradores
};

export const prestamoService = {
  getAll: (params = {}) => api.get('/prestamos', { params }),
  getPrestamos: (params = {}) => api.get('/prestamos', { params }),
  getPrestamo: (id) => api.get(`/prestamos/${id}`),
  getById: (id) => api.get(`/prestamos/${id}`),
  getHistorial: (id) => api.get(`/prestamos/${id}/historial`),
  createPrestamo: (data) => api.post('/prestamos', data),
  create: (data) => api.post('/prestamos', data),
  updatePrestamo: (id, data) => api.put(`/prestamos/${id}`, data),
  update: (id, data) => api.put(`/prestamos/${id}`, data),
  editarPrestamo: (id, data) => api.put(`/prestamos/${id}`, data),
  delete: (id) => api.delete(`/prestamos/${id}`),
  eliminarPrestamo: (id) => api.delete(`/prestamos/${id}`),
  eliminarPermanente: (id) => api.delete(`/prestamos/${id}/eliminar-permanente`),  // NUEVO: Eliminación permanente
  calcularPrestamo: (data) => api.post('/prestamos/calcular', data),
  calcular: (data) => api.post('/prestamos/calcular', data),
  crearReenganche: (id, data) => api.post(`/prestamos/${id}/reenganche`, data),
  reenganche: (id, data) => api.post(`/prestamos/${id}/reenganche`, data)
};

export const pagoService = {
  getAll: (params = {}) => api.get('/pagos', { params }),
  getPagos: (params = {}) => api.get('/pagos', { params }),
  getPago: (id) => api.get(`/pagos/${id}`),
  getById: (id) => api.get(`/pagos/${id}`),
  create: (data) => api.post('/pagos', data),
  createPago: (data) => api.post('/pagos', data),
  update: (id, data) => api.put(`/pagos/${id}`, data),
  delete: (id) => api.delete(`/pagos/${id}`),
  getPagosByPrestamo: (prestamoId) => api.get(`/pagos/prestamo/${prestamoId}`),
  getByPrestamo: (prestamoId) => api.get(`/pagos/prestamo/${prestamoId}`),
  abonoCapital: (data) => api.post('/pagos/abono-capital', data),  // Abono al capital con recalculación
  updateFechaPago: (id, data) => api.put(`/pagos/${id}/fecha`, data)  // Actualizar solo la fecha de pago
};

export const reporteService = {
  getDashboard: () => api.get('/reportes/dashboard'),
  getCobrosDiarios: (params = {}) => api.get('/reportes/cobros-diarios', { params }),
  getClientesMorosos: () => api.get('/reportes/clientes-morosos'),
  getDebenPagarHoy: () => api.get('/reportes/deben-pagar-hoy'),
  getPrestamosAtrasados: () => api.get('/reportes/prestamos-atrasados'),
  getPrestamosVencidos: () => api.get('/reportes/prestamos-vencidos')
};

export const garantiaService = {
  getAll: (params = {}) => api.get('/garantias', { params }),
  getById: (id) => api.get(`/garantias/${id}`),
  create: (data) => api.post('/garantias', data),
  update: (id, data) => api.put(`/garantias/${id}`, data),
  delete: (id) => api.delete(`/garantias/${id}`),
  getByCliente: (clienteId) => api.get(`/garantias?cliente_id=${clienteId}`),
  vincularPrestamo: (clienteId, data) => api.post(`/garantias/cliente/${clienteId}/vincular`, data),
  getStats: () => api.get('/garantias/stats')
};

export const documentoService = {
  generar: async (data) => {
    const response = await api.post('/documentos/generar', data, {
      responseType: 'blob'
    });
    return response;
  },
  descargar: (blob, nombreArchivo) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

export const finanzasService = {
  // Gastos
  getGastos: (params = {}) => api.get('/finanzas/gastos', { params }),
  getGasto: (id) => api.get(`/finanzas/gastos/${id}`),
  createGasto: (data) => api.post('/finanzas/gastos', data),
  updateGasto: (id, data) => api.put(`/finanzas/gastos/${id}`, data),
  deleteGasto: (id) => api.delete(`/finanzas/gastos/${id}`),
  aprobarGasto: (id) => api.post(`/finanzas/gastos/${id}/aprobar`),

  // Tipos de Egresos
  getTiposEgresos: () => api.get('/finanzas/tipos-egresos'),
  createTipoEgreso: (data) => api.post('/finanzas/tipos-egresos', data),
  updateTipoEgreso: (id, data) => api.put(`/finanzas/tipos-egresos/${id}`, data),

  // Suplidores
  getSuplidores: (params = {}) => api.get('/finanzas/suplidores', { params }),
  createSuplidor: (data) => api.post('/finanzas/suplidores', data),
  updateSuplidor: (id, data) => api.put(`/finanzas/suplidores/${id}`, data),
  deleteSuplidor: (id) => api.delete(`/finanzas/suplidores/${id}`),

  // Cuentas por pagar
  getCuentasPagar: (params = {}) => api.get('/finanzas/cuentas-pagar', { params }),
  getCuentaPagar: (id) => api.get(`/finanzas/cuentas-pagar/${id}`),
  createCuentaPagar: (data) => api.post('/finanzas/cuentas-pagar', data),
  updateCuentaPagar: (id, data) => api.put(`/finanzas/cuentas-pagar/${id}`, data),
  deleteCuentaPagar: (id) => api.delete(`/finanzas/cuentas-pagar/${id}`),
  pagarCuentaPagar: (id, data) => api.post(`/finanzas/cuentas-pagar/${id}/pagar`, data),

  // Gastos fijos
  getGastosFijos: (params = {}) => api.get('/finanzas/gastos-fijos', { params }),
  createGastoFijo: (data) => api.post('/finanzas/gastos-fijos', data),
  updateGastoFijo: (id, data) => api.put(`/finanzas/gastos-fijos/${id}`, data),
  deleteGastoFijo: (id) => api.delete(`/finanzas/gastos-fijos/${id}`),
  generarDesdeGastoFijo: (id, data = {}) => api.post(`/finanzas/gastos-fijos/${id}/generar`, data),

  // Bancos
  getBancos: (params = {}) => api.get('/finanzas/bancos', { params }),
  createBanco: (data) => api.post('/finanzas/bancos', data),

  // Dashboard
  getDashboardFinanzas: (params = {}) => api.get('/finanzas/dashboard', { params })
};

// Servicios de Caja Chica
export const cajaChicaService = {
  // Movimientos
  getMovimientos: (params = {}) => api.get('/caja-chica', { params }),
  getMovimiento: (id) => api.get(`/caja-chica/${id}`),
  createMovimiento: (data) => api.post('/caja-chica', data),

  // Balance
  getBalance: (params = {}) => api.get('/caja-chica/balance', { params }),

  // Resúmenes
  getResumenMensual: (params = {}) => api.get('/caja-chica/resumen-mensual', { params })
};

// Servicios de Facturas
export const facturasService = {
  getFacturas: (params = {}) => api.get('/facturas-suplidores', { params }),
  getFactura: (id) => api.get(`/facturas-suplidores/${id}`),
  createFactura: (data) => api.post('/facturas-suplidores', data),
  updateFactura: (id, data) => api.put(`/facturas-suplidores/${id}`, data),
  deleteFactura: (id) => api.delete(`/facturas-suplidores/${id}`)
};

// Servicios de Bancos
export const bancosService = {
  getBancos: (params = {}) => api.get('/bancos', { params }),
  getBanco: (id) => api.get(`/bancos/${id}`),
  createBanco: (data) => api.post('/bancos', data),
  updateBanco: (id, data) => api.put(`/bancos/${id}`, data),
  deleteBanco: (id) => api.delete(`/bancos/${id}`),
  depositar: (id, data) => api.post(`/bancos/${id}/depositar`, data),
  retirar: (id, data) => api.post(`/bancos/${id}/retirar`, data),
  getEstadoCuenta: (id, params = {}) => api.get(`/bancos/${id}/estado-cuenta`, { params }),
  updateTransaccion: (bancoId, transaccionId, data) => api.put(`/bancos/${bancoId}/transacciones/${transaccionId}`, data),
  deleteTransaccion: (bancoId, transaccionId) => api.delete(`/bancos/${bancoId}/transacciones/${transaccionId}`)
};

// Servicios de Transacciones
export const transaccionesService = {
  getTransacciones: (params = {}) => api.get('/transacciones', { params }),
  getTransaccion: (id) => api.get(`/transacciones/${id}`),
  createTransaccion: (data) => api.post('/transacciones', data),
  updateTransaccion: (id, data) => api.put(`/transacciones/${id}`, data),
  deleteTransaccion: (id) => api.delete(`/transacciones/${id}`)
};

// Servicios de Tipos de Egresos
export const tiposEgresosService = {
  getTiposEgresos: (params = {}) => api.get('/tipos-egresos', { params }),
  getTipoEgreso: (id) => api.get(`/tipos-egresos/${id}`),
  createTipoEgreso: (data) => api.post('/tipos-egresos', data),
  updateTipoEgreso: (id, data) => api.put(`/tipos-egresos/${id}`, data),
  deleteTipoEgreso: (id) => api.delete(`/tipos-egresos/${id}`)
};

// Servicios de Suplidores
export const suplidoresService = {
  getSuplidores: (params = {}) => api.get('/suplidores', { params }),
  getSuplidor: (id) => api.get(`/suplidores/${id}`),
  createSuplidor: (data) => api.post('/suplidores', data),
  updateSuplidor: (id, data) => api.put(`/suplidores/${id}`, data),
  deleteSuplidor: (id) => api.delete(`/suplidores/${id}`)
};

// Servicios de Cuadre de Caja
export const cuadreCajaService = {
  getCuadres: (params = {}) => api.get('/cuadre-caja', { params }),
  getCuadreHoy: () => api.get('/cuadre-caja/hoy'),
  getCuadrePorFecha: (fecha) => api.get(`/cuadre-caja/fecha/${fecha}`),
  getResumenSemanal: (params = {}) => api.get('/cuadre-caja/resumen-semanal', { params }),
  cerrarCaja: (data) => api.post('/cuadre-caja/cerrar', data)
};

// Servicios de Flujo de Caja
export const flujoCajaService = {
  getFlujo: (params = {}) => api.get('/flujo-caja', { params }),
  getResumen: (params = {}) => api.get('/flujo-caja/resumen', { params })
};

// Servicios de Cuentas por Pagar
export const cuentasPagarService = {
  getCuentas: (params = {}) => api.get('/cuentas-pagar', { params }),
  getCuenta: (id) => api.get(`/cuentas-pagar/${id}`),
  createCuenta: (data) => api.post('/cuentas-pagar', data),
  updateCuenta: (id, data) => api.put(`/cuentas-pagar/${id}`, data),
  deleteCuenta: (id) => api.delete(`/cuentas-pagar/${id}`),
  pagarCuenta: (id, data) => api.post(`/cuentas-pagar/${id}/pagar`, data)
};

// Servicios de Contabilidad
export const contabilidadService = {
  // Cuadre de pagos
  getCuadrePagos: (params) => api.get('/contabilidad/cuadre-pagos', { params }),
  getCobradores: () => api.get('/contabilidad/cobradores'),

  // Cuadre de rutas
  getCuadreRutas: (params) => api.get('/contabilidad/cuadre-rutas', { params }),
  getRutas: () => api.get('/contabilidad/rutas'),

  // Calculadora
  calcularCuota: (data) => api.post('/contabilidad/calcular-cuota', data),

  // Resumen
  getResumen: () => api.get('/contabilidad/resumen')
};

// Servicios de Configuración
export const configuracionService = {
  // Obtener configuración completa
  getConfiguracion: () => api.get('/configuracion'),

  // Actualizar secciones
  updateConfiguracion: (seccion, data) => api.put(`/configuracion/${seccion}`, data),

  // Logo
  uploadLogo: (formData) => api.post('/configuracion/logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  deleteLogo: () => api.delete('/configuracion/logo'),

  // Contraseña
  cambiarPassword: (data) => api.post('/configuracion/password', data),

  // Datos auxiliares
  getProvincias: () => api.get('/configuracion/provincias')
};

// ============================================
// WHATSAPP SERVICE
// ============================================
export const whatsappService = {
  // Mensajes
  enviarMensaje: (data) => api.post('/whatsapp/enviar', data),
  enviarMasivo: (data) => api.post('/whatsapp/enviar-masivo', data),
  getMensajes: (params) => api.get('/whatsapp/mensajes', { params }),
  getMensaje: (id) => api.get(`/whatsapp/mensajes/${id}`),
  deleteMensaje: (id) => api.delete(`/whatsapp/mensajes/${id}`),
  generarRecordatorios: () => api.post('/whatsapp/recordatorios'),

  // Plantillas
  getPlantillas: (params) => api.get('/whatsapp/plantillas', { params }),
  getPlantilla: (id) => api.get(`/whatsapp/plantillas/${id}`),
  createPlantilla: (data) => api.post('/whatsapp/plantillas', data),
  updatePlantilla: (id, data) => api.put(`/whatsapp/plantillas/${id}`, data),
  deletePlantilla: (id) => api.delete(`/whatsapp/plantillas/${id}`),
  previewPlantilla: (id, data) => api.post(`/whatsapp/plantillas/${id}/preview`, data),

  // Configuración
  getConfig: () => api.get('/whatsapp/config-simple'),
  updateConfig: (data) => api.put('/whatsapp/config-simple', data),

  // Estado y conexión
  getStatus: () => api.get('/whatsapp/status'),
  generateQR: () => api.post('/whatsapp/generate-qr'),
  restart: () => api.post('/whatsapp/restart'),

  // Estadísticas
  getEstadisticas: () => api.get('/whatsapp/estadisticas'),

  // Test
  enviarTest: (numero) => api.post('/whatsapp/test', { numero })
};

export const exchangeService = {
  getDashboard: () => api.get('/exchange/dashboard'),
  getMonedas: () => api.get('/exchange/monedas'),
  updateMoneda: (codigo, data) => api.put(`/exchange/monedas/${codigo}`, data),
  getInventario: () => api.get('/exchange/inventario'),
  listOperaciones: (params = {}) => api.get('/exchange/operaciones', { params }),
  createOperacion: (data) => api.post('/exchange/operaciones', data),
  getInyecciones: (params = {}) => api.get('/exchange/inyecciones', { params }),
  createInyeccion: (data) => api.post('/exchange/inyecciones', data),
  getFlujoDiario: (params = {}) => api.get('/exchange/reportes/flujo-diario', { params }),
  getGanancias: (params = {}) => api.get('/exchange/reportes/ganancias', { params }),
  getTicketOperacion: (id) => api.get(`/exchange/operaciones/${id}/ticket`),
  exportFlujoDiario: (params = {}) => api.get('/exchange/reportes/flujo-diario/export', {
    params,
    responseType: 'blob'
  }),
  exportGanancias: (params = {}) => api.get('/exchange/reportes/ganancias/export', {
    params,
    responseType: 'blob'
  }),
  getAlertas: () => api.get('/exchange/alertas'),
  runCierre: () => api.post('/exchange/cierres'),
  getConfig: () => api.get('/exchange/config'),
  updateConfig: (data) => api.put('/exchange/config', data)
};

export const permisosService = {
  // Usuarios
  getUsuarios: () => api.get('/permisos/usuarios'),
  getUsuario: (id) => api.get(`/permisos/usuario/${id}`),
  createUsuario: (data) => api.post('/permisos/usuarios', data),
  updateUsuario: (id, data) => api.put(`/permisos/usuario/${id}`, data),
  deleteUsuario: (id) => api.delete(`/permisos/usuario/${id}`),

  // Roles y permisos
  getRoles: () => api.get('/permisos/roles'),
  updateRol: (id, data) => api.put(`/permisos/usuario/${id}/rol`, data),
  updatePermisos: (id, data) => api.put(`/permisos/usuario/${id}/permisos`, data),
  updateEstado: (id, data) => api.put(`/permisos/usuario/${id}/estado`, data),
  
  // Cambio de contraseñas
  cambiarPasswordPropia: (data) => api.put('/permisos/cambiar-password', data),
  cambiarPasswordUsuario: (id, data) => api.put(`/permisos/usuario/${id}/cambiar-password`, data)
};

export default api;

